import React from "react";
import styled, { css, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";

import { Icon } from "./Icon";
import { getPartnerCode } from "../utils/partnerCode";

const CRM_LEADS_ENDPOINT = "https://crm.mangosoftwarehouse.com/api/public/leads";
const RASCUNHO_KEY = "mango-rascunho-diagnostico";
const RASCUNHO_TTL_MS = 6 * 60 * 60 * 1000;
const PEDIDO_KEY = "mango-ultimo-pedido";

function salvarUltimoPedido(pedido) {
    try {
        window.sessionStorage.setItem(PEDIDO_KEY, JSON.stringify({ ...pedido, em: Date.now() }));
    } catch (error) {
        // navegador sem storage
    }
}

function getDigits(value) {
    return String(value || "").replace(/\D/g, "");
}

function mascaraTelefone(value) {
    if (value.trim().startsWith("+")) {
        return value.replace(/[^\d+\s()-]/g, "").slice(0, 20);
    }

    const digits = getDigits(value).slice(0, 11);

    if (!digits) return "";
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function lerRascunho() {
    try {
        const cru = window.localStorage.getItem(RASCUNHO_KEY);
        if (!cru) return null;
        const d = JSON.parse(cru);
        if (!d || !d.em || Date.now() - d.em > RASCUNHO_TTL_MS) return null;
        if (!d.nome && !d.tel && !d.caso && !d.email) return null;
        return d;
    } catch (error) {
        return null;
    }
}

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 30px;
    border-radius: 22px;
    background: #ffffff;
    border: 1px solid var(--line);

    @media only screen and (max-width: 640px) {
        padding: 22px;
        border-radius: 18px;
    }
`;

const StepLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.88rem;
    font-weight: 800;
    color: var(--ink);
`;

const OpcoesRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const OpcaoChip = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    min-height: 42px;
    padding: 0 14px;
    border-radius: 12px;
    font-size: 14.5px;
    cursor: pointer;
    font-family: inherit;

    ${({ $on }) => ($on
        ? css`border: 1.5px solid var(--brand); background: #f2ecfb; color: var(--brand-2); font-weight: 700;`
        : css`border: 1.5px solid #ddd6ee; background: #fff; color: var(--ink); font-weight: 600;`)}
`;

const OpcaoHint = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: var(--success);
`;

const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 14px;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 800;
    color: var(--ink);

    ${({ $optional }) => $optional && css`
        font-weight: 700;
        color: var(--muted);

        span { font-weight: 600; }
    `}
`;

const inputBase = css`
    min-height: 54px;
    padding: 0 16px;
    border-radius: 12px;
    background: #fff;
    color: var(--ink);
    font-size: 16px;
    font-weight: 500;
    outline: none;
    font-family: inherit;
    border: 1.5px solid #ddd6ee;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &:focus {
        outline: 2px solid var(--brand);
        outline-offset: 1px;
    }

    ${({ $error }) => $error && css`
        border-color: var(--danger);
        background: #fffafc;
    `}

    ${({ $warning }) => $warning && css`
        border-color: #e0b877;
        background: #fffdf8;
    `}
`;

const Input = styled.input`${inputBase}`;

const Textarea = styled.textarea`
    ${inputBase}
    min-height: 104px;
    padding: 14px 16px;
    line-height: 1.55;
    resize: vertical;
`;

const HoneypotInput = styled.input`
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    opacity: 0;
`;

const FieldError = styled.span`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.45;
    color: ${({ $warning }) => ($warning ? "var(--warning)" : "var(--danger)")};
`;

const ResumoErro = styled.p`
    margin: 0;
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 14px 16px;
    border-radius: 12px;
    background: var(--danger-bg);
    border: 1px solid var(--danger-line);
    font-size: 14px;
    font-weight: 700;
    line-height: 1.5;
    color: var(--danger);
`;

const RascunhoBox = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #f2ecfb;
    border: 1px solid #e2daf5;
`;

const RascunhoTexto = styled.span`
    font-size: 14px;
    font-weight: 700;
    line-height: 1.5;
    color: var(--brand-2);
`;

const RascunhoBotao = styled.button`
    align-self: flex-start;
    min-height: 36px;
    padding: 0 12px;
    margin-top: 6px;
    border-radius: 10px;
    border: 1.5px solid #d9d0ee;
    background: #fff;
    color: var(--brand-2);
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
`;

const FalhaBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 18px;
    border-radius: 12px;
    background: var(--danger-bg);
    border: 1px solid var(--danger-line);
`;

const FalhaTitulo = styled.strong`
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: 15.5px;
    font-weight: 800;
    line-height: 1.4;
    color: var(--danger);
`;

const FalhaTexto = styled.p`
    margin: 0;
    font-size: 14.5px;
    font-weight: 600;
    line-height: 1.55;
    color: #8c2a5c;
`;

const FalhaAcoes = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

const TentarButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 18px;
    border: none;
    border-radius: 12px;
    background: var(--danger);
    color: #fff;
    font-size: 15px;
    font-weight: 800;
    cursor: pointer;
    font-family: inherit;
`;

const FalhaLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 16px;
    border-radius: 12px;
    border: 1.5px solid var(--danger-line);
    color: #8c2a5c;
    font-size: 15px;
    font-weight: 700;
`;

const SubmitButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 56px;
    border: none;
    border-radius: 999px;
    color: #fff;
    font-size: 16.5px;
    font-weight: 800;
    font-family: inherit;
    cursor: ${({ disabled }) => (disabled ? "progress" : "pointer")};
    background: ${({ disabled }) => (disabled ? "#8a6ae8" : "var(--brand)")};
    transition: background-color 0.2s ease, transform 0.2s ease;

    ${({ disabled }) => !disabled && css`
        &:hover { background: var(--brand-strong); }
    `}

    .material-symbols-rounded {
        ${({ disabled }) => disabled && css`animation: ${spin} 1s linear infinite;`}
    }
`;

const FinePrint = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.55;
    color: var(--muted);

    a {
        font-weight: 700;
        color: var(--brand-2);
    }
`;

function FormularioDiagnostico({
    situacao,
    exemplo,
    permitirCurto = false,
    opcoes,
    onOpcaoSelect,
    trocarHref
}) {
    const navigate = useNavigate();
    const [campos, setCampos] = React.useState({ nome: "", tel: "", caso: "", email: "", isca: "" });
    const [erros, setErros] = React.useState({});
    const [enviando, setEnviando] = React.useState(false);
    const [falhou, setFalhou] = React.useState(false);
    const [semConexao, setSemConexao] = React.useState(false);
    const [rascunhoRecuperado, setRascunhoRecuperado] = React.useState(false);
    const idPrefix = React.useId();

    React.useEffect(() => {
        const rascunho = lerRascunho();
        if (rascunho) {
            setCampos((atual) => ({ ...atual, ...rascunho }));
            setRascunhoRecuperado(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function salvarRascunho(patch) {
        try {
            const proximo = { ...campos, ...patch };
            if (!proximo.nome && !proximo.tel && !proximo.caso && !proximo.email) {
                window.localStorage.removeItem(RASCUNHO_KEY);
                return;
            }
            window.localStorage.setItem(RASCUNHO_KEY, JSON.stringify({
                nome: proximo.nome, tel: proximo.tel, caso: proximo.caso, email: proximo.email, em: Date.now()
            }));
        } catch (error) {
            // navegador sem storage
        }
    }

    function limparRascunho() {
        try {
            window.localStorage.removeItem(RASCUNHO_KEY);
        } catch (error) {
            // idem
        }
    }

    function handleChange(chave) {
        return (event) => {
            const valor = chave === "tel" ? mascaraTelefone(event.target.value) : event.target.value;
            setCampos((atual) => ({ ...atual, [chave]: valor }));
            salvarRascunho({ [chave]: valor });
            setErros((atual) => {
                if (!atual[chave]) return atual;
                const proximo = { ...atual };
                delete proximo[chave];
                return proximo;
            });
            setFalhou(false);
        };
    }

    function validar() {
        const proximosErros = {};
        if (!campos.nome.trim()) proximosErros.nome = "Diga como podemos te chamar.";

        const digitos = getDigits(campos.tel);
        if (!digitos) proximosErros.tel = "Precisamos de um telefone para responder.";
        else if (digitos.length < 10) proximosErros.tel = "Faltam dígitos: informe DDD e número.";

        const emailValor = campos.email.trim();
        if (emailValor && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValor)) {
            proximosErros.email = "Confira o e-mail: parece incompleto.";
        }

        return proximosErros;
    }

    function dicaCaso() {
        if (permitirCurto) return "";
        const tamanho = campos.caso.trim().length;
        if (tamanho === 0 || tamanho >= 10) return "";
        return "Ajuda se você contar em uma frase — mas pode enviar assim também.";
    }

    async function handleSubmit(event) {
        event.preventDefault();
        if (campos.isca) return;

        const validacao = validar();
        if (Object.keys(validacao).length) {
            setErros(validacao);
            setFalhou(false);
            const primeiroErro = ["nome", "tel", "email"].find((campo) => validacao[campo]);
            const alvo = document.getElementById(`${idPrefix}-${primeiroErro}`);
            if (alvo) alvo.focus();
            return;
        }

        setErros({});
        setEnviando(true);
        setFalhou(false);

        const offline = typeof navigator !== "undefined" && navigator.onLine === false;

        try {
            if (offline) {
                throw new Error("offline");
            }

            const partnerCode = getPartnerCode();
            const endpoint = partnerCode
                ? `${CRM_LEADS_ENDPOINT}?partner_code=${encodeURIComponent(partnerCode)}`
                : CRM_LEADS_ENDPOINT;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    nome: campos.nome.trim(),
                    email: campos.email.trim() || null,
                    telefone: getDigits(campos.tel) || null,
                    empresa: null,
                    mensagem: `Situação: ${situacao}. ${campos.caso.trim()}`
                })
            });

            if (!response.ok) {
                throw new Error("falha-servidor");
            }

            limparRascunho();
            const pedido = {
                nome: campos.nome.trim().split(" ")[0],
                telefone: campos.tel.trim(),
                situacao
            };
            salvarUltimoPedido(pedido);
            navigate("/obrigado", { state: pedido });
        } catch (error) {
            setEnviando(false);
            setFalhou(true);
            setSemConexao(offline || error.message === "offline");
        }
    }

    const qtdErros = Object.keys(erros).length;
    const dica = dicaCaso();
    const conteudoWa = `Olá! Sou ${campos.nome.trim() || "…"}. Situação: ${situacao}. ${campos.caso.trim()}`;
    const waFalha = `https://wa.me/554191150056?text=${encodeURIComponent(conteudoWa)}`;

    return (
        <Form noValidate onSubmit={handleSubmit} aria-label="Formulário de diagnóstico">
            <Field>
                <StepLabel>
                    <Icon name="check_circle" size="19px" color="var(--success)" />
                    1. O que está travando
                </StepLabel>

                {opcoes && opcoes.length > 0 ? (
                    <>
                        <OpcoesRow role="group" aria-label="Situação escolhida">
                            {opcoes.map((opcao) => (
                                <OpcaoChip
                                    key={opcao.id}
                                    type="button"
                                    $on={opcao.on}
                                    aria-pressed={opcao.on}
                                    onClick={() => onOpcaoSelect && onOpcaoSelect(opcao.id)}
                                >
                                    {opcao.label}
                                </OpcaoChip>
                            ))}
                        </OpcoesRow>
                        <OpcaoHint>Marcado a partir da sua escolha acima — pode trocar aqui.</OpcaoHint>
                    </>
                ) : (
                    <OpcoesRow style={{ alignItems: "center", justifyContent: "space-between" }}>
                        <strong style={{ fontSize: "15.5px", fontWeight: 800, color: "var(--brand-2)" }}>{situacao}</strong>
                        {trocarHref && (
                            <a href={trocarHref} style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--brand-2)", textDecoration: "underline" }}>
                                Trocar situação
                            </a>
                        )}
                    </OpcoesRow>
                )}
            </Field>

            <FieldGrid>
                <Field>
                    <Label htmlFor={`${idPrefix}-nome`}>2. Seu nome</Label>
                    <Input
                        id={`${idPrefix}-nome`}
                        name="nome"
                        type="text"
                        autoComplete="name"
                        required
                        aria-invalid={Boolean(erros.nome)}
                        value={campos.nome}
                        onChange={handleChange("nome")}
                        placeholder="Como podemos te chamar"
                        $error={Boolean(erros.nome)}
                    />
                    {erros.nome && <FieldError><Icon name="error" size="16px" />{erros.nome}</FieldError>}
                </Field>

                <Field>
                    <Label htmlFor={`${idPrefix}-tel`}>3. WhatsApp ou telefone</Label>
                    <Input
                        id={`${idPrefix}-tel`}
                        name="telefone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        required
                        aria-invalid={Boolean(erros.tel)}
                        value={campos.tel}
                        onChange={handleChange("tel")}
                        placeholder="(41) 99999-9999"
                        $error={Boolean(erros.tel)}
                    />
                    {erros.tel && <FieldError><Icon name="error" size="16px" />{erros.tel}</FieldError>}
                </Field>
            </FieldGrid>

            <Field>
                <Label htmlFor={`${idPrefix}-caso`}>4. Em uma frase, como isso acontece hoje</Label>
                <Textarea
                    id={`${idPrefix}-caso`}
                    name="caso"
                    value={campos.caso}
                    onChange={handleChange("caso")}
                    placeholder={exemplo}
                    $warning={Boolean(dica)}
                />
                {dica && <FieldError $warning><Icon name="lightbulb" size="16px" />{dica}</FieldError>}
            </Field>

            <Field>
                <Label htmlFor={`${idPrefix}-email`} $optional>5. E-mail <span>(opcional — para o caso de o telefone não completar)</span></Label>
                <Input
                    id={`${idPrefix}-email`}
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    aria-invalid={Boolean(erros.email)}
                    value={campos.email}
                    onChange={handleChange("email")}
                    placeholder="email@empresa.com"
                    $error={Boolean(erros.email)}
                />
                {erros.email && <FieldError><Icon name="error" size="16px" />{erros.email}</FieldError>}
            </Field>

            <HoneypotInput
                type="text"
                name="assunto_extra"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                value={campos.isca}
                onChange={(event) => setCampos((atual) => ({ ...atual, isca: event.target.value }))}
            />

            {qtdErros > 1 && (
                <ResumoErro role="alert">
                    <Icon name="error" size="19px" />
                    Faltam {qtdErros} campos para enviar. Eles estão marcados acima.
                </ResumoErro>
            )}

            {rascunhoRecuperado && (
                <RascunhoBox>
                    <Icon name="history" size="19px" color="var(--brand-2)" />
                    <div>
                        <RascunhoTexto>Recuperamos o que você tinha começado a escrever neste navegador.</RascunhoTexto>
                        <br />
                        <RascunhoBotao
                            type="button"
                            onClick={() => {
                                limparRascunho();
                                setCampos({ nome: "", tel: "", caso: "", email: "", isca: "" });
                                setRascunhoRecuperado(false);
                            }}
                        >
                            Começar em branco
                        </RascunhoBotao>
                    </div>
                </RascunhoBox>
            )}

            {falhou && (
                <FalhaBox role="alert">
                    <FalhaTitulo>
                        <Icon name={semConexao ? "wifi_off" : "cloud_off"} size="20px" />
                        {semConexao ? "Você está sem conexão agora" : "Não conseguimos enviar o seu pedido"}
                    </FalhaTitulo>
                    <FalhaTexto>
                        {semConexao
                            ? "O que você escreveu continua aqui e fica guardado neste navegador. Quando a internet voltar, é só tocar em tentar de novo — ou ligar, que funciona mesmo sem dados."
                            : "O que você escreveu continua aqui, nada foi perdido. Foi uma falha do nosso lado ao receber o pedido. Pode tentar de novo agora — ou, se preferir não esperar, chamar no WhatsApp com o mesmo conteúdo."}
                    </FalhaTexto>
                    <FalhaAcoes>
                        <TentarButton type="submit">
                            <Icon name="refresh" size="19px" />
                            Tentar de novo
                        </TentarButton>
                        {!semConexao && (
                            <FalhaLink href={waFalha} target="_blank" rel="noreferrer">
                                <Icon name="chat" size="19px" />
                                Mandar pelo WhatsApp
                            </FalhaLink>
                        )}
                        <FalhaLink href="tel:+554191150056">
                            <Icon name="call" size="19px" />
                            Ligar
                        </FalhaLink>
                    </FalhaAcoes>
                </FalhaBox>
            )}

            <SubmitButton type="submit" disabled={enviando}>
                {enviando ? "Enviando o seu pedido…" : "Enviar e receber o diagnóstico"}
                <Icon name={enviando ? "progress_activity" : "arrow_outward"} size="20px" />
            </SubmitButton>

            <FinePrint>
                Resposta em até 1 dia útil, pelo telefone que você preencher — e por e-mail, se informado. Sua empresa pode estar em qualquer cidade do Brasil ou de Portugal.
            </FinePrint>
            <FinePrint>
                Usamos seu nome, telefone e a descrição do caso apenas para responder a este pedido — sem lista de e-mail e sem compartilhar com terceiros. Guardamos por 24 meses, e apagamos antes se você pedir. Detalhes na <a href="/privacidade">política de privacidade</a>.
            </FinePrint>
        </Form>
    );
}

export { FormularioDiagnostico, CRM_LEADS_ENDPOINT, PEDIDO_KEY };
export default FormularioDiagnostico;
